import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, MailCheck, ArrowLeft } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Seo } from '@/components/Seo';
import { forgotPasswordSchema } from '@/lib/auth-validation';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Persistent, not a toast -- matches the signup "check your email" screen.
  // Shown for every successful *request* regardless of whether the address
  // actually has an account (see handleSubmit -- Supabase's own
  // resetPasswordForEmail() never reveals that either way).
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = forgotPasswordSchema.safeParse({ email });
    if (!parsed.success) {
      toast({ title: 'Check your details', description: parsed.error.issues[0].message, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(parsed.data.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSubmitting(false);
    if (error) {
      // resetPasswordForEmail() never reports "no account with that email" --
      // Supabase resolves the same way whether or not the address is
      // registered, specifically so this can't be used to enumerate
      // accounts. Any error reaching here is a genuine service-level
      // failure (network, rate limit), not an existence signal, so a plain
      // retry message is correct and doesn't leak anything account-specific.
      console.error('Password reset request failed:', error);
      toast({
        title: 'Something went wrong',
        description: "We couldn't send that right now. Please try again in a moment.",
        variant: 'destructive',
      });
      return;
    }
    setSubmittedEmail(parsed.data.email);
  };

  return (
    <Layout>
      <Seo title="Forgot password | Swell Breaks" description="Reset your Swell Breaks password." path="/forgot-password" noIndex />
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-md">
          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">Forgot your password?</h1>
            <p className="text-muted-foreground text-sm">We'll help you get back in.</p>
          </div>

          <Card className="border-border/50 bg-card border-foreground/20">
            {submittedEmail ? (
              <CardContent className="pt-8 pb-8 text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <MailCheck className="h-7 w-7 text-primary" />
                </div>
                <CardTitle className="font-serif text-xl mb-2">Check your email</CardTitle>
                <p className="text-sm text-muted-foreground mb-1">
                  If an account exists for
                </p>
                <p className="text-sm font-medium text-foreground mb-4">{submittedEmail}</p>
                <p className="text-sm text-muted-foreground mb-6">
                  we've sent a password reset link. Click it to choose a new password.
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/auth">
                    <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                    Back to sign in
                  </Link>
                </Button>
              </CardContent>
            ) : (
              <>
                <CardHeader className="pb-2">
                  <CardTitle className="font-serif text-xl mb-1">Reset your password</CardTitle>
                  <CardDescription>Enter the email address on your account.</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="fp-email">Email</Label>
                      <Input
                        id="fp-email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Send reset link
                    </Button>
                    <Link
                      to="/auth"
                      className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Back to sign in
                    </Link>
                  </form>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default ForgotPassword;
